import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, ClipboardList, BookOpen, MessageCircleHeart, Feather,
  TrendingUp, Users, Sparkles, ArrowRight, ArrowLeft, X, Clock, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  {
    icon: Sparkles, kicker: "Welcome", title: "Welcome to Sisterhood+",
    body: "Sisterhood+ is your personal growth space — designed to help you strengthen your mental wellness, build healthier relationships, and develop practical life skills through evidence-based learning and personalized AI support.\n\nYou don't need to use everything today. We'll guide you one step at a time.",
    href: null,
  },
  {
    icon: Home, kicker: "Step 1 · Home", title: "Your personal dashboard",
    body: "Home is where each day starts. Every day, Sisterhood+ recommends your next best step based on your recent activity, goals, progress, and check-ins.",
    href: "/home",
  },
  {
    icon: ClipboardList, kicker: "Step 2 · Daily Check-In", title: "Under one minute a day",
    body: "The more consistently you check in, the more personalized your recommendations, wellness insights, and AI coaching become.",
    href: "/wellness",
  },
  {
    icon: BookOpen, kicker: "Step 3 · Sisterhood University", title: "Evidence-based lessons",
    body: "Sisterhood University contains lessons created with mental wellness professionals in mind. Each one is practical, concise, and designed to build real-life skills.",
    href: "/learn",
  },
  {
    icon: MessageCircleHeart, kicker: "Step 4 · AI Coach", title: "Apply what you learn",
    body: "Ask questions. Reflect on challenges. Practice conversations. Receive personalized guidance. The more you use Sisterhood+, the more personalized your experience becomes.",
    href: "/coach",
  },
  {
    icon: Sparkles, kicker: "Step 5 · Daily Affirmation", title: "A word made just for you",
    body: "Each day, your Coach writes a personalized affirmation based on your recent reflections. Save the ones you love, and share them with the community.",
    href: "/home",
  },
  {
    icon: Feather, kicker: "Step 6 · Journal", title: "Your private reflection space",
    body: "Writing regularly can help you recognize emotional patterns, celebrate progress, and deepen your self-awareness.",
    href: "/journal",
  },
  {
    icon: TrendingUp, kicker: "Step 7 · Progress", title: "Track your growth over time",
    body: "View check-ins, wellness trends, lessons completed, growth plans, and weekly reviews.\n\nProgress is about consistency, not perfection.",
    href: "/progress",
  },
  {
    icon: Users, kicker: "Step 8 · Community", title: "A circle of sisters",
    body: "Share what you're learning, celebrate each other, and be held by a safe, moderated community of women walking a similar path.",
    href: "/community",
  },
  {
    icon: ShieldCheck, kicker: "Step 9 · Good to know", title: "Support — not a substitute for care",
    body: "Sisterhood+ offers supportive guidance and reflection. It is not therapy, medical, or crisis care. Your journal and coaching conversations stay private and are never sold.\n\nIf you're ever in danger or thinking about harming yourself, call or text 988 (Suicide & Crisis Lifeline), or 911 in an emergency.",
    href: null,
  },
];

const FINISH = {
  icon: Sparkles, title: "You're ready.",
  body: "You don't have to do everything today. Simply complete today's recommendation — Sisterhood+ will continue guiding your journey from there.\n\nRemember: Sisterhood+ is a growth and wellness space, not a substitute for professional care.",
  cta: "Go to my dashboard", href: "/home",
};

export default function OnboardingTour() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (user && localStorage.getItem("sh_tour_completed") !== "1") setOpen(true);
  }, [user]);

  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener("sh:replay-tour", handler);
    return () => window.removeEventListener("sh:replay-tour", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => { localStorage.setItem("sh_tour_completed", "1"); setOpen(false); };
  const finishAndGo = () => { close(); nav(FINISH.href); };

  if (!open) return null;

  const isFinish = step >= STEPS.length;
  const s = isFinish ? FINISH : STEPS[step];
  const isFirst = step === 0;
  const total = STEPS.length;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 lg:p-8"
      style={{ backgroundColor: "rgba(41,27,45,0.72)" }} data-testid="onboarding-tour">
      <AnimatePresence mode="wait">
        <motion.div key={step}
          initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md lg:max-w-xl relative rounded-3xl bg-white"
          style={{ boxShadow: "0 40px 100px -20px rgba(41,27,45,0.5)" }}>
          {!isFinish && (
            <button data-testid="tour-close" onClick={close} aria-label="Skip tour"
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#291B2D]/50 hover:text-[#291B2D] hover:bg-[#F7F2EA] transition-smooth">
              <X size={16} />
            </button>
          )}
          <div className="p-7 lg:p-10">
            <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(200,155,85,0.16)" }}>
              <s.icon size={26} className="text-[#C89B55]" strokeWidth={1.5} />
            </div>
            {!isFinish ? (
              <>
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#C89B55] font-semibold">{s.kicker}</p>
                <div className="flex items-center gap-2 text-[11px] text-[#291B2D]/45 mt-1">
                  <span>Step {step + 1} of {total}</span>
                  <span className="w-1 h-1 rounded-full bg-[#291B2D]/20" />
                  <span className="inline-flex items-center gap-1"><Clock size={10} strokeWidth={1.75} />Under 2 min</span>
                </div>
              </>
            ) : (
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#C89B55] font-semibold">Finish</p>
            )}
            <h2 className="font-display text-2xl lg:text-3xl text-[#291B2D] mt-2 leading-snug">{s.title}</h2>
            <p className="text-sm lg:text-base text-[#291B2D]/70 mt-3 leading-relaxed whitespace-pre-line">{s.body}</p>
            {!isFinish && (
              <div className="flex items-center gap-1.5 mt-6">
                {STEPS.map((_, i) => (
                  <span key={i} className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: i === step ? 32 : 16, backgroundColor: i === step ? "#C89B55" : i < step ? "rgba(41,27,45,0.4)" : "rgba(41,27,45,0.1)" }} />
                ))}
              </div>
            )}
            <div className="mt-7 flex items-center justify-between gap-3">
              {!isFinish ? (
                <>
                  <button data-testid="tour-skip" onClick={close}
                    className="text-sm text-[#291B2D]/50 hover:text-[#291B2D] transition-smooth">Skip tour</button>
                  <div className="flex items-center gap-2">
                    {!isFirst && (
                      <button data-testid="tour-prev" onClick={() => setStep(step - 1)}
                        className="inline-flex items-center gap-1.5 text-sm bg-[#F7F2EA] text-[#291B2D] rounded-full px-4 py-2.5 hover:bg-[#EAE5D9] transition-smooth">
                        <ArrowLeft size={13} />Back
                      </button>
                    )}
                    <button data-testid="tour-next" onClick={() => setStep(step + 1)}
                      className="inline-flex items-center gap-2 text-sm bg-[#291B2D] text-white rounded-full px-5 py-2.5 hover:bg-[#352238] transition-smooth">
                      {isFirst ? "Start tour" : (step === total - 1 ? "Finish" : "Next")} <ArrowRight size={14} />
                    </button>
                  </div>
                </>
              ) : (
                <button data-testid="tour-finish" onClick={finishAndGo}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm bg-[#C89B55] text-[#291B2D] rounded-full px-6 py-3 hover:bg-[#B38743] transition-smooth">
                  {FINISH.cta} <ArrowRight size={14} />
                </button>
              )}
            </div>
            {!isFinish && s.href && !isFirst && (
              <button data-testid="tour-visit-page" onClick={() => { close(); nav(s.href); }}
                className="mt-3 text-xs text-[#291B2D]/50 hover:text-[#291B2D] inline-flex items-center gap-1 transition-smooth">
                Open this page now <ArrowRight size={11} />
              </button>
            )}
            <p className="mt-6 text-[11px] text-[#291B2D]/40 leading-relaxed border-t border-[#291B2D]/5 pt-4" data-testid="tour-disclaimer">
              Sisterhood+ is not a substitute for professional care. In crisis, call or text <a href="tel:988" className="underline">988</a>, or <a href="tel:911" className="underline">911</a> in an emergency.
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
